import React from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  OutlinedInputProps,
  Theme,
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  styled,
  withStyles,
} from '@material-ui/core/styles';

export interface StyledInputProps extends OutlinedInputProps {
  helperText?: React.ReactNode;
  description?: React.ReactNode;
  warning?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    hidden: { display: 'none' },
    warningInput: {
      backgroundColor: '#FDF7EC',
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#F4B142 !important',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#5e9bff !important',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#F4B142 !important',
      },
    },
    warningHelperText: { color: '#F4B142 !important' },
  }),
);

export const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  color: '#777777',
  fontSize: '14px',
  lineHeight: '32px',
  '&.MuiInputLabel-formControl': {
    position: 'static',
    transform: 'initial',
  },
  '&.MuiInputLabel-shrink': {
    transform: 'initial',
  },
  '& .MuiInputLabel-asterisk': {
    color: '#e91e63',
  },
  '&.Mui-focused': {
    color: '#777777',
  },
  '&.Mui-error': {
    color: '#777777',
  },
  '&.Mui-disabled': {
    color: '#777777',
  },
}));

export const StyledOutlinedInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      'label + &': {
        marginTop: theme.spacing(0),
        padding: theme.spacing(0),
      },
      '& :-webkit-autofill': {
        '-webkit-box-shadow': '0 0 0 1000px white inset',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: '1px solid #c2cad8',
        borderRadius: '3px',
      },
      '&.Mui-error': {
        backgroundColor: '#fce8ef',
        '&.Mui-focused': {
          backgroundColor: '#ffffff',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e91e63',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#5e9bff',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e91e63',
        },
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#c2cad8',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: '1px solid #5e9bff',
      },
      '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderWidth: '0px',
      },
    },
    input: {
      minHeight: '32px',
      boxSizing: 'border-box',
      padding: theme.spacing(0.75, 1),
      fontSize: '14px',
      '&.Mui-disabled': {
        backgroundColor: '#EDEFF3',
        color: '#333333',
        borderRadius: '3px',
      },
    },
  }),
)(OutlinedInput);

export const StyledHelperText = withStyles(
  createStyles({
    root: {
      fontSize: '14px',
      color: '#e91e63',
      margin: 0,
      '&.Mui-error': {
        color: '#e91e63',
      },
    },
  }),
)(FormHelperText);

export const StyledDescription = withStyles(
  createStyles({
    root: {
      marginTop: '5px',
      '& .MuiTypography-root': {
        color: '#7F7F7F',
        fontSize: '12px',
        '& span': {
          display: 'block',
        },
        '& strong': {
          fontWeight: 'bold',
        },
        '& .MuiLink-root': {
          color: '#1565C0',
        },
      },
    },
  }),
)(Box);

export const StyledInput: React.FC<StyledInputProps> = ({
  className = '',
  id,
  label,
  required = false,
  error = false,
  disabled = false,
  hidden = false,
  warning = false,
  helperText,
  description,
  ...props
}: StyledInputProps) => {
  const classes = useStyles();

  return (
    <FormControl
      required={required}
      error={error}
      disabled={disabled}
      className={`${hidden ? classes.hidden : ''} ${className}`}
    >
      <StyledInputLabel htmlFor={id}>{label}</StyledInputLabel>
      <StyledOutlinedInput
        {...props}
        id={id}
        className={warning ? classes.warningInput : ''}
      />
      {helperText && (error || warning) && (
        <StyledHelperText
          children={helperText}
          className={warning ? classes.warningHelperText : ''}
        />
      )}
      {description && <StyledDescription children={description} />}
    </FormControl>
  );
};
